import { Drawer, DrawerBody, DrawerProvider } from '@cko/primitives';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { DrawerInstance } from '@cko/dashboard-shared';
import { ViewInstance } from '../../config/shared/types';
import { ViewRenderer } from '../../layout/ViewRenderer';

type DrawerContextType = {
  drawerInstance: DrawerInstance | undefined;
  showDrawerInstance: (drawerInstance: DrawerInstance) => void;
  closeDrawerInstance: () => void;
};

const DrawerContext = createContext<DrawerContextType>({
  drawerInstance: undefined,
  showDrawerInstance: () => {},
  closeDrawerInstance: () => {},
});

export const useDrawerContext = () => useContext(DrawerContext);

export const DrawerContextProvider = DrawerContext.Provider;

export const DrawerManager = (props: { children: ReactNode }) => {
  const [drawerInstanceValue, setDrawerInstance] = useState<DrawerInstance | undefined>(undefined);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleCloseDrawerInstance = () => {
      setDrawerInstance(undefined);
      setIsOpen(false);
      // This function will be used when we want to perform a custom action when closing the Drawer, e.g., clearing states, etc.
      if (drawerInstanceValue?.drawerNavigationProps?.onCloseDrawer) {
        drawerInstanceValue?.drawerNavigationProps?.onCloseDrawer();
      }
  };

  const handleShowDrawerInstance = (drawerInstance: DrawerInstance) => {
    if(drawerInstance && drawerInstance?.viewId !== undefined) {
      setDrawerInstance(drawerInstance);
      setIsOpen(true);
    } else {
      // eslint-disable-next-line no-console
      console.warn('drawerInstance is undefined');
    }
  };

  const viewId = drawerInstanceValue?.viewId;
  const headerActions = drawerInstanceValue?.drawerNavigationProps?.headerActions;
  const headerTitle = drawerInstanceValue?.drawerNavigationProps?.headerTitle;
  const viewInstance: ViewInstance | undefined =
    viewId !== undefined ? { view_id: viewId, type: 'view' } : undefined;

  const viewProps = drawerInstanceValue?.viewProps;
  return (
    <DrawerProvider open={isOpen} onOpenChange={handleCloseDrawerInstance}>
      <>
        <DrawerContextProvider
          value={{
            drawerInstance: drawerInstanceValue,
            showDrawerInstance: handleShowDrawerInstance,
            closeDrawerInstance: handleCloseDrawerInstance,
          }}
        >
          {props.children}
          <Drawer
            onClose={handleCloseDrawerInstance}
            headerActions={headerActions}
            headerTitle={headerTitle}
          >
            <DrawerBody>
              {viewInstance && <ViewRenderer viewInstance={viewInstance} viewProps={viewProps} />}
            </DrawerBody>
          </Drawer>
        </DrawerContextProvider>
      </>
    </DrawerProvider>
  );
};
